import { NOME_OPERACAO, TELAS_SO_99PLAY, type Eu, type IdOperacao } from '@bi-evox/contrato';
import { useEffect, useState } from 'react';
import { Navigate, NavLink, Outlet, useLocation, useSearchParams } from 'react-router';
import { useEu, useSituacao } from '../api/consultas';
import { operacao, token } from '../api/cliente';
import { fmtDuracao } from '../lib/formato';
import { ABAS } from '../lib/telas';
import { Icone } from './Icone';
import { SeletorPeriodo } from './SeletorPeriodo';


/** Telas de um dia só têm o próprio seletor de dia; Saques mostra os mais recentes; Acessos e Conteúdo não são dado da operação. */
const SEM_PERIODO = new Set(['/risco', '/risco/evento', '/saques', '/acessos', '/conteudo']);

/** Faixa de atraso: um dia "fraco" pode ser só réplica atrasada, não queda de movimento. */
function FaixaAtraso() {
  const { data } = useSituacao();
  const atraso = data?.atrasoReplicaSegundos;
  if (atraso == null || atraso < 600) return null;
  return (
    <div className="faixa-atraso" role="status">
      A réplica da Evox está {fmtDuracao(atraso)} atrasada. Os números das últimas horas ainda vão mudar.
    </div>
  );
}

/** Acesso sem nenhuma tela: não acontece com os perfis de hoje, mas não deixa a tela em branco. */
function SemTelas({ nome, motivo }: { nome: string; motivo?: string }) {
  return (
    <div className="login">
      <div className="aviso" role="status">
        <strong>O acesso {nome} não abre nenhuma tela.</strong>
        <p>{motivo ?? 'Peça para incluir um perfil no seu acesso.'}</p>
        <button type="button" onClick={token.apagar}>
          Sair
        </button>
      </div>
    </div>
  );
}

/** Switch de operação no topo: só aparece para quem vê mais de uma (o admin). */
function SwitchOperacao({ operacoes, atual }: { operacoes: IdOperacao[]; atual: IdOperacao }) {
  if (operacoes.length < 2) return null;
  return (
    <div className="barra-operacao">
      <span className="mudo">Operação</span>
      <div className="segmentado" role="group" aria-label="Operação">
        {operacoes.map((o) => (
          <button key={o} type="button" aria-pressed={atual === o} onClick={() => atual !== o && operacao.gravar(o)}>
            {NOME_OPERACAO[o]}
          </button>
        ))}
      </div>
    </div>
  );
}

/** A operação gravada neste navegador, se o acesso ainda a vê; senão, a primeira do acesso. */
const operacaoValida = (eu: Eu): IdOperacao | undefined => {
  const gravada = operacao.ler();
  return gravada && eu.operacoes.includes(gravada) ? gravada : eu.operacoes[0];
};

const CHAVE_MENU = 'bi-evox:menu-aberto';

/** Menu recolhido por padrão; a escolha fica neste navegador. */
function useMenuAberto(): [boolean, () => void] {
  const [aberto, setAberto] = useState(() => {
    try {
      return localStorage.getItem(CHAVE_MENU) === '1';
    } catch {
      return false;
    }
  });
  const alternar = () =>
    setAberto((a) => {
      try {
        localStorage.setItem(CHAVE_MENU, a ? '0' : '1');
      } catch {
        /* sem localStorage: vale só nesta página */
      }
      return !a;
    });
  return [aberto, alternar];
}

export function Layout() {
  const [params] = useSearchParams();
  const [menuAberto, alternarMenu] = useMenuAberto();
  const { pathname } = useLocation();
  const eu = useEu();
  const qs = params.toString() ? `?${params}` : '';
  const atual = eu.data ? operacaoValida(eu.data) : undefined;
  // grava a escolha para o cabeçalho ir explícito; trocar remonta as telas (main.tsx)
  useEffect(() => {
    if (atual && operacao.ler() !== atual) operacao.gravar(atual);
  }, [atual]);
  if (eu.isError) {
    return (
      <div className="login">
        <div className="aviso erro" role="alert">
          <strong>Não consegui conferir o seu acesso.</strong>
          <p>{eu.error.message}</p>
          <button type="button" onClick={() => void eu.refetch()}>
            Tentar de novo
          </button>
        </div>
      </div>
    );
  }
  if (!eu.data) return <div className="carregando" aria-busy="true">Carregando…</div>;
  if (!atual) return <SemTelas nome={eu.data.nome} motivo="A operação do seu acesso não está ligada no servidor." />;
  // Só as abas do perfil (e da operação); endereço de outra tela (ou que não existe) leva à primeira aba liberada.
  const telas = eu.data.telas.filter((t) => atual === '99play' || !TELAS_SO_99PLAY.includes(t));
  const abas = ABAS.filter((a) => telas.includes(a.tela));
  const telaAtual = ABAS.find((a) => a.para === pathname)?.tela ?? 'visao-geral';
  if (!telas.includes(telaAtual)) {
    return abas[0] ? <Navigate to={`${abas[0].para}${qs}`} replace /> : <SemTelas nome={eu.data.nome} />;
  }
  return (
    <div className={`app${menuAberto ? '' : ' menu-recolhido'}`}>
      <aside className="lateral">
        <div className="marca">
          <span className="marca-simbolo" aria-hidden="true">
            {atual === '99play' ? '99' : 'TP'}
          </span>
          <span className="rotulo" title={NOME_OPERACAO[atual]}>
            BI <span>{atual === '99play' ? 'Evox' : NOME_OPERACAO[atual]}</span>
          </span>
        </div>
        <button
          type="button"
          className="alternar-menu"
          onClick={alternarMenu}
          aria-expanded={menuAberto}
          aria-label={menuAberto ? 'Recolher o menu' : 'Abrir o menu'}
          data-dica={menuAberto ? undefined : 'Abrir o menu'}
        >
          <Icone nome={menuAberto ? 'recolher' : 'expandir'} />
          <span className="rotulo">Recolher</span>
        </button>
        <nav aria-label="Seções">
          {abas.map((a) => (
            <NavLink
              key={a.para}
              to={`${a.para}${qs}`}
              end
              className={({ isActive }) => (isActive ? 'ativo' : undefined)}
              aria-label={menuAberto ? undefined : a.rotulo}
              data-dica={menuAberto ? undefined : a.rotulo}
            >
              <Icone nome={a.icone} />
              <span className="rotulo">{a.rotulo}</span>
            </NavLink>
          ))}
        </nav>
        <button
          type="button"
          className="sair"
          onClick={token.apagar}
          aria-label={`Sair (${eu.data.nome})`}
          title={`Entrou como ${eu.data.nome} · ${eu.data.perfis.join(', ')}`}
          data-dica={menuAberto ? undefined : `Sair (${eu.data.nome})`}
        >
          <Icone nome="sair" />
          <span className="rotulo">Sair ({eu.data.nome})</span>
        </button>
      </aside>
      <div className="conteudo">
        <SwitchOperacao operacoes={eu.data.operacoes} atual={atual} />
        <FaixaAtraso />
        <main>
          {!SEM_PERIODO.has(pathname) && <SeletorPeriodo />}
          <Outlet />
        </main>
      </div>
    </div>
  );
}
